/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  generatePath,
  useMatch,
  useNavigate,
  useOutletContext,
  useParams,
  useLocation,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import { useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import isEqual from 'lodash.isequal';
import queryString from 'query-string';
import {
  PermissionEnum,
  IconSelected,
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  IconRight,
  IconUpload,
  Table,
  ButtonPrimary,
  DateInputRange,
  IconCalendar,
  SidePanelLoader,
  BlacklistData,
  BlacklistDataContent,
  ResponseError,
  ChangeLogs,
  PermissionDenied,
  BlacklistUserInfoData,
  BlacklistUserInfo,
  formatBlacklistItem,
  responseErrorsResolver,
  InputNumber,
  Select,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { getAmlGeneralInfo, clearAmlChangeLogs } from '../store/slices/aml/aml';
import AmlGeneralInfo from '../../../components/corporate/aml/general-info';
import AmlAccountsTransactionStatement from '../../customers/aml-statements/aml-statements';
import OngoingMonitoring from '../../customers/ongoing-monitoring/ongoing-monitoring';
import OngoingPanelContent from '../../customers/ongoing-monitoring/ongoing-panel-content';
import InitialCdd from '../../../components/corporate/aml/initial-cdd';
import RiskScoring from '../../../components/corporate/aml/risk-scoring';
import Card from '../../../components/partial/card/card';
import {
  useLazyGetCorporatelUserScoreQuery,
  useCreateCorporateUserScoreMutation,
  useLazyGetCorporateAmlUpdatingItemQuery,
  useLazyGetCorporateLastCalculatingScoreQuery,
  useUpdateCorporateRangesMutation,
  useLazyGetCorporateOngoingMonitoringQuery,
  useLazyGetCorporateOngoingMonitoringItemQuery,
  useApplyCorporateOngoingWarningActionMutation,
  useBatchApplyCorporateOngoingWarningActionMutation,
  useResetCorporateWarningGroupActionMutation,
  useLazyGetCorporateWarningGroupWithPaginationQuery,
  useLazyGetCorporateOngoingWarningQuery,
  useCreateCorporateManualRiskScoreMutation,
  useUpdateCorporateInfoMutation,
} from '../store/services/corporate';
import { useLazyGetCustomersApplicationSchemesQuery } from '../../../store/dictionaries/services';
import {
  useGetOngoingMoitoringPdfMutation,
  useLazyGetCustomersChangeLogsQuery,
  useUpdateCustomerQuestionnaireMutation,
  useLazyGetCustomersAMLScoresHistoryQuery,
  useLazyGetCustomersRelationShipToCustomerDictionaryQuery,
  useLazyGetCustomersSourceOfWealthForCorporateDictionaryQuery,
  useLazyGetCustomersBusinessActivitiesDictionaryQuery,
  useLazyGetRegulatedCompanyMainClientsDictionaryQuery,
  useLazyGetCustomersSalesChannelsDictionaryQuery,
  useLazyGetCustomersSourceOfFundsQuery,
  useLazyGetCustomersSourceOfWealthQuery,
  useLazyGetCustomersAnnualIncomeDictionaryQuery,
  useLazyGetCustomersAccountsCurrenciesQuery,
  useCreateCustomersAccountsCurrencyMutation,
  useUpdateCustomersAccountsCurrencyMutation,
  useDeleteCustomersAccountsCurrencyMutation,
  useLazyGetCustomersAccountsFlowsQuery,
  useCreateCustomersAccountsFlowMutation,
  useUpdateCustomersAccountsFlowMutation,
  useDeleteCustomersAccountsFlowMutation,
  useLazyGetCustomersExpectedFundsOriginsQuery,
  useCreateCustomersExpectedFundsOriginsMutation,
  useUpdateCustomersExpectedFundsOriginsMutation,
  useDeleteCustomersExptectedFundsOriginsMutation,
  useLazyGetCustomersTypeOfAccountsDictionaryQuery,
} from '../../customers/store/services/customers';
import formatOngoingMonitroing from '../../individual/helpers/format-ongoing-monitoring';
import {
  ApplicationScheme,
  CustomerRanges,
  CustomerScore,
  CustomerScoreUpdateStatus,
  CustomerType,
  OngoingMonitring,
  OrganizationType,
  Questionnarie,
} from '../../customers/types';
import { ongoingColumns, ongoingColumnsLabels } from './variables/ongoing';
import AdditionalInformation from '../../../components/corporate/aml/additional-infromation';
import { setNotificationVisible } from '../../../../../../store/slice';
import AmlChangeLogs from '../../customers/aml-change-logs/change-logs';
import { CorporateOverviewGeneral } from '../types';
import {
  AccountsCurrencies,
  AccountsCurrenciesFormValues,
  AccountsFlowsFormValues,
  ExpectedFundsOriginsFormValues,
} from '../../new-application/types';
import getRegulatedCompanyQuestionnaireBody from './helpers/get-regulated-company-questionnaire-body';

import ROUTES from '../../../routes';

import cn from '../corporate.module.css';

let timeInterval: NodeJS.Timeout;
let manualRiskScoreTimeout: NodeJS.Timeout;

const Aml = (): React.JSX.Element => {
  const {
    corporateCustomers: {
      info: {
        general: {
          baseInformation,
          businessPartners,
          changeLogs,
          changeLogsPagination,
        },
        aml: {
          amlGeneralInfo,
          aml,
          amlUpdateStatus,
          nextReviewDate,
          ongoingMonitoring,
          ongoingMonitoringItem,
          amlChangeLogs,
          amlChangeLogsPagination,
          relationshipToTheCustomerDictionary,
          sourceOfWealth,
          businessActivities,
          customerMainClients,
          salesChannels,
          customerSourceOfFunds,
          customerSourceOfWealth,
          annualIncomes,
          accountsCurrencies,
          accountsFlows,
          expectedFundsOrigins,
          typeOfAccounts,
        },
      },
    },
    customersDictionaries: {
      cardsEnabled,
      currentEmployee,
      applicationSchemes,
      riskScoreRanges,
    },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);
  const {
    activePermissions,
    baseCurrency,
    currenciesList: currencies,
    currencies: currenciesList,
    countriesList,
  } = useAppSelector((state) => state.root);

  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [exportPdfPanelVisible, setExportPdfPanelVisible] = useState(false);
  const [showOngoing, setShowOngoing] = useState(false);
  const [ongoingItemPanelVisible, setOngoingItemPanelVisible] = useState(false);
  const [blacklistDataVisible, setBlacklistDataVisible] = useState(false);
  const [blacklistData, setBlacklistData] = useState<BlacklistData | undefined>(
    undefined,
  );
  const [applyError, setApplyError] = useState('');
  const [changeLogsPanelIsOpen, setChangeLogPanelIsOpen] = useState(false);
  const [currentApplicationScheme, setCurrentApplicationScheme] = useState<
    ApplicationScheme | undefined
  >(undefined);
  const [selectedWarningId, setSelectedWarningId] = useState('');
  const [selectedWarningGroup, setSelectedWarningGroup] = useState('');
  const [blacklistItemVisible, setBlacklistItemVisible] = useState(false);
  const [blacklistItem, setBlacklistDataItem] = useState<
    BlacklistUserInfoData | undefined
  >(undefined);
  const [totalRiskScore, setTotalRiskScore] = useState(0);
  const [totalRiskScoreLevel, setTotalRiskScoreLevel] = useState('');
  const [manualScoreSidepanelVisible, setManualScoreSidepanelVisible] =
    useState(false);
  const [manualRiskScoreError, setManualRiskScoreError] = useState('');
  const [
    riskScoreChangeLogsSidepanelVisible,
    setRiskScoreChangeLogsSidepanelVisible,
  ] = useState(false);
  const [totalIncomeError, setTotalIncomeError] = useState('');
  const [accountsCurrenciesValues, setAccountsCurrenciesValues] = useState<
    AccountsCurrenciesFormValues[]
  >([
    {
      id: 'accounts-currency-1',
      accountOpeningPurpose: '',
      currencyId: '',
      plannedTransactionNature: '',
    },
  ]);
  const [selectedAccountsCurrencyId, setSelectedAccountsCurrencyId] =
    useState('');
  const [accountsFlowsValues, setAccountsFlowsValues] = useState<
    AccountsFlowsFormValues[]
  >([]);
  const [expectedFundsOriginsValues, setExpectedFundsOriginsValues] = useState<
    ExpectedFundsOriginsFormValues[]
  >([
    {
      id: 'expected-funds-origins-1',
      currencyId: '',
      mainCounterpartiesAndWebsites: '',
      countryOfIncorporationId: '',
      counterpartyBankCountryIds: [],
      activityCountryIds: [],
      mainActivities: '',
      transactionReason: '',
      transactionType: 'INCOMING',
    },
    {
      id: 'expected-funds-origins-2',
      currencyId: '',
      mainCounterpartiesAndWebsites: '',
      countryOfIncorporationId: '',
      counterpartyBankCountryIds: [],
      activityCountryIds: [],
      mainActivities: '',
      transactionReason: '',
      transactionType: 'OUTGOING',
    },
  ]);
  const [selectedExpectedFundsOriginsId, setSelectedFundsOriginsId] =
    useState('');

  const [exportDate, setExportDate] = useState<string[]>([]);

  const { search } = useLocation();

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
    clearErrors,
    setError,
  } = useForm<Questionnarie>();

  const questionnaireFields = watch();

  const [getCorporateScore, { isLoading }] =
    useLazyGetCorporatelUserScoreQuery();
  const [getCorporateAmlUpdatingItem] =
    useLazyGetCorporateAmlUpdatingItemQuery();
  const [getCorporateLastCalculaingScore] =
    useLazyGetCorporateLastCalculatingScoreQuery();
  const [createCorporateScore, { isSuccess, isLoading: requestInProgress }] =
    useCreateCorporateUserScoreMutation();
  const [
    getCorporateOngoingMonitoring,
    { isLoading: ongoingIsLoading, isFetching: ongoingIsFetching },
  ] = useLazyGetCorporateOngoingMonitoringQuery();
  const [
    getCorporateOngoingMonitoringItem,
    { isLoading: ongoingItemIsLoading, isFetching: ongoingItemIsFetching },
  ] = useLazyGetCorporateOngoingMonitoringItemQuery();
  const [getApplicationsSchemes] = useLazyGetCustomersApplicationSchemesQuery();
  const [defaultQuestionnaire, setDefaultQuestionaire] =
    useState<Questionnarie>();

  const [updateQuestionnaire, { isLoading: questionnnaireRequestInProgress }] =
    useUpdateCustomerQuestionnaireMutation();
  const [updateCorporateRanges, { isLoading: updateRangesInProgress }] =
    useUpdateCorporateRangesMutation();
  const [applyCorporateOngoingWarning, { isLoading: applyWarningInProgress }] =
    useApplyCorporateOngoingWarningActionMutation();
  const [
    batchCorporateOngoingWarning,
    {
      isLoading: batchApplyWarningInProgress,
      isSuccess: batchApplyWarningIsSuccess,
    },
  ] = useBatchApplyCorporateOngoingWarningActionMutation();
  const [
    getOngoingMonitoringPdf,
    {
      isLoading: ongoingMonitoringPdfIsLoading,
      isSuccess: ongoingMonitoringPdfIsSuccess,
    },
  ] = useGetOngoingMoitoringPdfMutation();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetCustomersChangeLogsQuery();
  const [
    resetCorporateTransactionWarning,
    {
      isLoading: resetTransactionsWarningIsLoading,
      isSuccess: resetTransactionsWarningIsSuccess,
    },
  ] = useResetCorporateWarningGroupActionMutation();
  const [
    getCorporateWarning,
    {
      isLoading: paginationInProgress,
      isFetching: paginationIsFetching,
      isSuccess: paginationIsSuccess,
    },
  ] = useLazyGetCorporateWarningGroupWithPaginationQuery();
  const [
    getCorporateOngoingWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetCorporateOngoingWarningQuery();
  const [
    createCorporateManualRiskScore,
    {
      isLoading: createRiskScoreIsLoading,
      isSuccess: createRiskScoreIsSuccess,
    },
  ] = useCreateCorporateManualRiskScoreMutation();
  const [
    getCustomerAmlScoresHistory,
    {
      isLoading: amlScoresHistoryIsLoading,
      isFetching: amlScoresHistoryIsFetching,
    },
  ] = useLazyGetCustomersAMLScoresHistoryQuery();
  const [updateCorporateInfo, { isLoading: updateInfoIsLoading }] =
    useUpdateCorporateInfoMutation();
  const [getRelationShipToCustomerDictionary] =
    useLazyGetCustomersRelationShipToCustomerDictionaryQuery();
  const [getSourceOfWealthForCorporateDictionary] =
    useLazyGetCustomersSourceOfWealthForCorporateDictionaryQuery();
  const [getBusinessActivitiesDictionary] =
    useLazyGetCustomersBusinessActivitiesDictionaryQuery();
  const [getRegulatedCompanyMainClientsDictionary] =
    useLazyGetRegulatedCompanyMainClientsDictionaryQuery();
  const [getSalesChannelsDictionary] =
    useLazyGetCustomersSalesChannelsDictionaryQuery();
  const [getCustomersSourceOfFundsDictionary] =
    useLazyGetCustomersSourceOfFundsQuery();
  const [getCustomersSourceOfWealth] = useLazyGetCustomersSourceOfWealthQuery();
  const [getCustomersAnnualIncomes] =
    useLazyGetCustomersAnnualIncomeDictionaryQuery();
  const [getCustomersTypeOfAccounts] =
    useLazyGetCustomersTypeOfAccountsDictionaryQuery();

  const [
    getAccountsCurrencies,
    {
      isLoading: accountsCurrenciesIsLoading,
      isFetching: accountsCurrenciesIsFetching,
    },
  ] = useLazyGetCustomersAccountsCurrenciesQuery();
  const [
    createAccountsCurrencies,
    { isLoading: createAccountsCurrenciesIsProgress },
  ] = useCreateCustomersAccountsCurrencyMutation();
  const [
    updateAccountsCurrencies,
    { isLoading: updateAccountsCurrenciesInProgress },
  ] = useUpdateCustomersAccountsCurrencyMutation();
  const [
    deleteAccountsCurrencies,
    {
      isLoading: deleteAccountsCurrenciesInProgress,
      isSuccess: deleteAccountsCurrenciesIsSuccess,
    },
  ] = useDeleteCustomersAccountsCurrencyMutation();
  const [
    getAccountsFlows,
    { isLoading: accountsFlowsIsLoading, isFetching: accountsFlowsIsFetching },
  ] = useLazyGetCustomersAccountsFlowsQuery();
  const [createAccountsFlows, { isLoading: createAccountsFlowsIsProgress }] =
    useCreateCustomersAccountsFlowMutation();
  const [updateAccountsFlows, { isLoading: updateAccountsFlowsInProgress }] =
    useUpdateCustomersAccountsFlowMutation();
  const [deleteAccountsFlows] = useDeleteCustomersAccountsFlowMutation();
  const [
    getExpectedFundsOrigins,
    {
      isLoading: expectedFundsOriginsIsLoading,
      isFetching: expectedFundsOriginsIsFetching,
    },
  ] = useLazyGetCustomersExpectedFundsOriginsQuery();
  const [
    createExpectedFundsOrigins,
    { isLoading: createExpectedFundsOriginsInProgress },
  ] = useCreateCustomersExpectedFundsOriginsMutation();
  const [
    updateExpectedFundsOrigins,
    { isLoading: updateExpectedFundsOriginsInProgress },
  ] = useUpdateCustomersExpectedFundsOriginsMutation();
  const [
    deleteExpectedFundsOrigins,
    {
      isLoading: deleteExpectedFundsOriginsInProgress,
      isSuccess: deleteExpectedFundsOriginsIsSuccess,
    },
  ] = useDeleteCustomersExptectedFundsOriginsMutation();

  const match = useMatch(ROUTES.CUSTOMERS_CORPORATE_AML);
  const { customerId } = useParams();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onSaveExpectedFundsOrigins = async () => {
    const expectedFundsOriginsForCreation: ExpectedFundsOriginsFormValues[] =
      [];
    const expectedFundsOriginsForUpdate: ExpectedFundsOriginsFormValues[] = [];

    expectedFundsOriginsValues?.forEach((item) => {
      if (item.id?.includes('expected-funds-origins')) {
        expectedFundsOriginsForCreation.push(item);
      } else {
        const currentExpectedFundsOrigins = expectedFundsOrigins?.find(
          (value) => value.id === item.id,
        );

        if (currentExpectedFundsOrigins) {
          if (
            !isEqual(item, {
              id: currentExpectedFundsOrigins.id,
              currencyId: currentExpectedFundsOrigins.currencyId,
              mainCounterpartiesAndWebsites:
                currentExpectedFundsOrigins.mainCounterpartiesAndWebsites,
              countryOfIncorporationId:
                currentExpectedFundsOrigins.countryOfIncorporationId,
              counterpartyBankCountryIds:
                currentExpectedFundsOrigins.counterpartyBankCountryIds,
              activityCountryIds:
                currentExpectedFundsOrigins.activityCountryIds,
              mainActivities: currentExpectedFundsOrigins.mainActivities,
              transactionReason: currentExpectedFundsOrigins.transactionReason,
              transactionType: currentExpectedFundsOrigins.transactionType,
            })
          ) {
            expectedFundsOriginsForUpdate.push(item);
          }
        }
      }
    });

    if (expectedFundsOriginsForCreation?.length) {
      await Promise.all(
        expectedFundsOriginsForCreation
          ?.filter((item) => item.currencyId)
          ?.map(async (item) => {
            const response = await createExpectedFundsOrigins({
              id: baseInformation?.questionnaire?.id as string,
              body: {
                currencyId: item.currencyId,
                mainCounterpartiesAndWebsites:
                  item.mainCounterpartiesAndWebsites || '',
                countryOfIncorporationId: item.countryOfIncorporationId || '',
                counterpartyBankCountryIds:
                  item.counterpartyBankCountryIds || '',
                activityCountryIds: item.activityCountryIds || '',
                mainActivities: item.mainActivities || '',
                transactionReason: item.transactionReason || '',
                transactionType: item.transactionType || '',
              },
            });

            return response;
          }),
      );
    }

    if (expectedFundsOriginsForUpdate?.length) {
      await Promise.all(
        expectedFundsOriginsForUpdate?.map(async (item) => {
          const response = await updateExpectedFundsOrigins({
            id: item.id as string,
            body: {
              currencyId: item.currencyId,
              mainCounterpartiesAndWebsites:
                item.mainCounterpartiesAndWebsites || '',
              countryOfIncorporationId: item.countryOfIncorporationId || '',
              counterpartyBankCountryIds: item.counterpartyBankCountryIds || '',
              activityCountryIds: item.activityCountryIds || '',
              mainActivities: item.mainActivities || '',
              transactionReason: item.transactionReason || '',
              transactionType: item.transactionType || '',
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveAccountsCurrencies = async () => {
    const accountsCurrenciesForCreation: AccountsCurrenciesFormValues[] = [];
    const accountsCurrenciesForUpdate: AccountsCurrenciesFormValues[] = [];

    accountsCurrenciesValues?.forEach((item) => {
      if (item.id?.includes('accounts-currency')) {
        accountsCurrenciesForCreation.push(item);
      } else {
        const currentAccountsCurrency = accountsCurrencies?.find(
          (currency) => currency.id === item.id,
        );

        if (currentAccountsCurrency) {
          if (
            !isEqual(item, {
              id: currentAccountsCurrency.id,
              currencyId: currentAccountsCurrency.currencyId,
              accountOpeningPurpose:
                currentAccountsCurrency.accountOpeningPurpose,
              plannedTransactionNature:
                currentAccountsCurrency.plannedTransactionNature,
            })
          ) {
            accountsCurrenciesForUpdate.push(item);
          }
        }
      }
    });

    if (accountsCurrenciesForCreation?.length) {
      await Promise.all(
        accountsCurrenciesForCreation
          ?.filter((item) => item.currencyId)
          ?.map(async (item) => {
            const response = await createAccountsCurrencies({
              id: baseInformation?.questionnaire?.id as string,
              body: {
                currencyId: item.currencyId,
                accountOpeningPurpose: item.accountOpeningPurpose || '',
                plannedTransactionNature: item.plannedTransactionNature || '',
              },
            });

            return response;
          }),
      );
    }

    if (accountsCurrenciesForUpdate?.length) {
      await Promise.all(
        accountsCurrenciesForUpdate?.map(async (item) => {
          const response = await updateAccountsCurrencies({
            id: item.id as string,
            body: {
              currencyId: item.currencyId,
              accountOpeningPurpose: item.accountOpeningPurpose || '',
              plannedTransactionNature: item.plannedTransactionNature || '',
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveAccountsFlows = async () => {
    const accountsFlowsForCreation: AccountsFlowsFormValues[] = [];
    const accountsFlowsForUpdate: AccountsFlowsFormValues[] = [];

    accountsFlowsValues?.forEach((item) => {
      if (item.id?.includes('accounts-flow')) {
        accountsFlowsForCreation.push(item);
      } else {
        const currentAccountsFlow = accountsFlows?.find(
          (flow) => flow.id === item.id,
        );

        if (currentAccountsFlow) {
          if (
            !isEqual(item, {
              id: currentAccountsFlow.id,
              transactionType: currentAccountsFlow.transactionType,
              currencyId: currentAccountsFlow.currencyId,
              expectedAnnualCreditTurnover:
                currentAccountsFlow.expectedAnnualCreditTurnover.toString(),
              maxTransferAmountPerTransaction:
                currentAccountsFlow.maxTransferAmountPerTransaction.toString(),
              transactionFrequency:
                currentAccountsFlow.transactionFrequency.toString(),
              transactionFrequencyPeriodMonths:
                currentAccountsFlow.transactionFrequencyPeriodMonths.toString(),
            })
          ) {
            accountsFlowsForUpdate.push(item);
          }
        }
      }
    });

    if (
      accountsFlowsForCreation?.filter(
        (item) =>
          item.expectedAnnualCreditTurnover &&
          item.maxTransferAmountPerTransaction &&
          item.transactionFrequency &&
          item.transactionFrequencyPeriodMonths,
      )?.length
    ) {
      await Promise.all(
        accountsFlowsForCreation
          ?.filter(
            (item) =>
              item.expectedAnnualCreditTurnover &&
              item.maxTransferAmountPerTransaction &&
              item.transactionFrequency &&
              item.transactionFrequencyPeriodMonths,
          )
          ?.map(async (item) => {
            const response = await createAccountsFlows({
              id: baseInformation?.questionnaire?.id as string,
              body: {
                transactionType: item.transactionType,
                currencyId: item.currencyId,
                expectedAnnualCreditTurnover: Number(
                  item.expectedAnnualCreditTurnover,
                ),
                maxTransferAmountPerTransaction: Number(
                  item.maxTransferAmountPerTransaction,
                ),
                transactionFrequency: Number(item.transactionFrequency),
                transactionFrequencyPeriodMonths: Number(
                  item.transactionFrequencyPeriodMonths,
                ),
              },
            });

            return response;
          }),
      );
    }

    if (accountsFlowsForUpdate?.length) {
      await Promise.all(
        accountsFlowsForUpdate?.map(async (item) => {
          const response = await updateAccountsFlows({
            id: item.id as string,
            body: {
              transactionType: item.transactionType,
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: Number(
                item.expectedAnnualCreditTurnover,
              ),
              maxTransferAmountPerTransaction: Number(
                item.maxTransferAmountPerTransaction,
              ),
              transactionFrequency: Number(item.transactionFrequency),
              transactionFrequencyPeriodMonths: Number(
                item.transactionFrequencyPeriodMonths,
              ),
            },
          });

          return response;
        }),
      );
    }
  };

  const onDeleteAccountCurrencies = async (id: string) => {
    if (id.includes('accounts-currency')) {
      setAccountsCurrenciesValues(
        accountsCurrenciesValues?.filter((item) => item.id !== id),
      );

      const currentAccountsCurrency = accountsCurrenciesValues?.find(
        (item) => item.id === id,
      );

      if (currentAccountsCurrency) {
        const currentAccountsFlows = accountsFlowsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        const currentExpectedFundsOrigins = expectedFundsOriginsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        if (currentAccountsFlows?.length) {
          setAccountsFlowsValues(
            accountsFlowsValues?.filter((item) =>
              currentAccountsFlows.every((flow) => item.id !== flow.id),
            ),
          );
        }

        if (currentExpectedFundsOrigins?.length) {
          setExpectedFundsOriginsValues(
            expectedFundsOriginsValues?.filter((item) =>
              currentExpectedFundsOrigins.every(
                (expectedFund) => item.id !== expectedFund.id,
              ),
            ),
          );
        }
      }
    } else {
      setSelectedAccountsCurrencyId(id);

      const currentAccountsCurrency = accountsCurrenciesValues?.find(
        (item) => item.id === id,
      );

      if (currentAccountsCurrency) {
        const currentAccountsFlows = accountsFlowsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        const currentExpectedFundsOrigins = expectedFundsOriginsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        if (currentAccountsFlows?.length) {
          await Promise.all(
            currentAccountsFlows
              ?.filter((item) => !item.id?.includes('accounts-flow'))
              ?.map(async (item) => {
                const response = await deleteAccountsFlows(item.id as string);

                return response;
              }),
          );

          if (
            currentAccountsFlows?.filter((item) =>
              item.id?.includes('accounts-flow'),
            ).length
          ) {
            const notCreatedAccountsFlow = currentAccountsFlows?.filter(
              (item) => item.id?.includes('accounts-flow'),
            );

            setAccountsFlowsValues(
              accountsFlowsValues?.filter((item) =>
                notCreatedAccountsFlow.every((flow) => item.id !== flow.id),
              ),
            );
          }
        }

        if (currentExpectedFundsOrigins?.length) {
          await Promise.all(
            currentExpectedFundsOrigins
              ?.filter((item) => !item.id?.includes('expected-funds-origins'))
              ?.map(async (item) => {
                const response = await deleteExpectedFundsOrigins(
                  item.id as string,
                );

                return response;
              }),
          );

          if (
            currentExpectedFundsOrigins?.filter((item) =>
              item.id?.includes('expected-funds-origins'),
            )?.length
          ) {
            const notCreatedExpectedFundsOrigins =
              currentExpectedFundsOrigins?.filter((item) =>
                item.id?.includes('expected-funds-origins'),
              );

            setExpectedFundsOriginsValues(
              expectedFundsOriginsValues?.filter((item) =>
                notCreatedExpectedFundsOrigins.every(
                  (expectedFundsOrigin) => item.id !== expectedFundsOrigin.id,
                ),
              ),
            );
          }
        }
      }

      await deleteAccountsCurrencies(id);
    }
  };

  const onDeleteExpectedFundsOrigins = async (id: string) => {
    if (id.includes('expected-funds-origins')) {
      setExpectedFundsOriginsValues(
        expectedFundsOriginsValues?.filter((item) => item.id !== id),
      );
    } else {
      setSelectedFundsOriginsId(id);

      await deleteExpectedFundsOrigins(id);
    }
  };

  const getCustomerAmlChangeLogsAction = async (cursor?: string) => {
    await getCustomerAmlScoresHistory({
      id: customerId as string,
      query: `?take=20&${cursor ? `&cursor=${cursor}` : ''}`,
    });
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=ORGANIZATION&entityId=${customerId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [customerId],
  );

  const createCorporateManualRiskScoreAction = async () => {
    setManualRiskScoreError('');

    const response = (await createCorporateManualRiskScore({
      id: customerId as string,
      totalRiskScore,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setManualRiskScoreError,
      });
    }
  };

  const getCorporateOngoingWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    const response = await getCorporateOngoingWarningById({
      orgId: customerId as string,
      warningId,
      id,
    });

    if (
      !blacklistData &&
      !blacklistItem &&
      !blacklistDataVisible &&
      !blacklistItemVisible &&
      response
    ) {
      if (response?.data?.data?.blacklistDoc) {
        setBlacklistData(response?.data?.data?.blacklistDoc);
        setBlacklistDataVisible(true);
      } else if (response?.data?.data?.blacklistItem) {
        setBlacklistDataItem(
          formatBlacklistItem(response?.data?.data?.blacklistItem),
        );
        setBlacklistItemVisible(true);
      }
    }
  };

  const handleCloseAmlChangeLogsPanel = () => {
    setRiskScoreChangeLogsSidepanelVisible(false);
    dispatch(clearAmlChangeLogs());
  };

  const getOngoingMonitoringPdfAction = async () => {
    const body: {
      createdDateFrom?: string;
      createdDateTo?: string;
    } = {};

    if (exportDate && exportDate.length) {
      body.createdDateFrom = new Date(exportDate[0]).toISOString();

      if (exportDate.length > 1) {
        body.createdDateTo = new Date(exportDate[1])?.toISOString();
      }
    }

    await getOngoingMonitoringPdf({
      id: customerId as string,
      body,
    });
  };

  const onResetTransactionWarningAction = async (
    warningId: string,
    id: string,
  ) => {
    setSelectedWarningId(warningId);

    const response = (await resetCorporateTransactionWarning({
      orgId: customerId as string,
      id,
      warningId,
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const onUpdateRange = (ranges: Record<string, string>) => {
    const updatedRanges: CustomerRanges = {};

    if (
      ranges.monthlyMaxValueIncomingPaymentRangeId !==
      baseInformation?.monthlyMaxValueIncomingPaymentRangeId
    ) {
      updatedRanges.monthlyMaxValueIncomingPaymentRangeId =
        ranges.monthlyMaxValueIncomingPaymentRangeId;
    }

    if (
      ranges.monthlyMaxValueOutgoingPaymentRangeId !==
      baseInformation?.monthlyMaxValueOutgoingPaymentRangeId
    ) {
      updatedRanges.monthlyMaxValueOutgoingPaymentRangeId =
        ranges.monthlyMaxValueOutgoingPaymentRangeId;
    }

    if (
      ranges.monthlyNumberIncomingTransactionsRangeId !==
      baseInformation?.monthlyNumberIncomingTransactionsRangeId
    ) {
      updatedRanges.monthlyNumberIncomingTransactionsRangeId =
        ranges.monthlyNumberIncomingTransactionsRangeId;
    }

    if (
      ranges.monthlyNumberOutgoingTransactionsRangeId !==
      baseInformation?.monthlyNumberOutgoingTransactionsRangeId
    ) {
      updatedRanges.monthlyNumberOutgoingTransactionsRangeId =
        ranges.monthlyNumberOutgoingTransactionsRangeId;
    }

    if (
      ranges.monthlyTurnoverRangeId !== baseInformation?.monthlyTurnoverRangeId
    ) {
      updatedRanges.monthlyTurnoverRangeId = ranges.monthlyTurnoverRangeId;
    }

    if (
      ranges.monthlyValueIncomingTransactionsRangeId !==
      baseInformation?.monthlyValueIncomingTransactionsRangeId
    ) {
      updatedRanges.monthlyValueIncomingTransactionsRangeId =
        ranges.monthlyValueIncomingTransactionsRangeId;
    }

    if (
      ranges.monthlyValueOutgoingTransactionsRangeId !==
      baseInformation?.monthlyValueOutgoingTransactionsRangeId
    ) {
      updatedRanges.monthlyValueOutgoingTransactionsRangeId =
        ranges.monthlyValueOutgoingTransactionsRangeId;
    }

    if (Object.keys(updatedRanges)?.length > 0) {
      updateCorporateRanges({
        id: customerId as string,
        body: updatedRanges,
      });
    }
  };

  const onUpdateCorporateInfo = (values: Partial<CorporateOverviewGeneral>) => {
    const body: Partial<CorporateOverviewGeneral> = {};

    const amlCtfComplianceProgram = Boolean(
      values.amlCtfComplianceProgram === 'yes',
    );

    if (values.purposeOfAccount !== baseInformation?.purposeOfAccount) {
      body.purposeOfAccount = values.purposeOfAccount;
    }

    if (
      !isEqual(
        values.expectedTypeOfClients,
        baseInformation?.expectedTypeOfClients,
      )
    ) {
      body.expectedTypeOfClients = values.expectedTypeOfClients;
    }

    if (
      !isEqual(
        values.requiredCurrenciesCryptoId,
        baseInformation?.requiredCurrenciesCryptoId,
      )
    ) {
      body.requiredCurrenciesCryptoId = values.requiredCurrenciesCryptoId;
    }

    if (amlCtfComplianceProgram !== baseInformation?.amlCtfComplianceProgram) {
      body.amlCtfComplianceProgram = amlCtfComplianceProgram;
    }

    if (Object.keys(body)?.length > 0) {
      updateCorporateInfo({
        id: customerId as string,
        body,
      });
    }
  };

  const batchApplyCorporateOngoingWarningAction = async ({
    id,
    action,
    comment,
    matchStatus,
  }: {
    id: string;
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const response = (await batchCorporateOngoingWarning({
      orgId: customerId as string,
      body: {
        warningGroupId: id,
        action,
        employeeId: currentEmployee?.id as string,
        comment,
        matchStatus,
      },
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const onResetQuestionnaire = () => {
    reset(defaultQuestionnaire);

    if (accountsCurrencies) {
      setAccountsCurrenciesValues(
        accountsCurrencies?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          accountOpeningPurpose: item.accountOpeningPurpose,
          plannedTransactionNature: item.plannedTransactionNature,
        })),
      );
    }

    if (accountsFlows) {
      setAccountsFlowsValues(
        accountsFlows?.map((item) => ({
          id: item.id,
          transactionType: item.transactionType,
          currencyId: item.currencyId,
          expectedAnnualCreditTurnover:
            item.expectedAnnualCreditTurnover.toString(),
          maxTransferAmountPerTransaction:
            item.maxTransferAmountPerTransaction.toString(),
          transactionFrequency: item.transactionFrequency.toString(),
          transactionFrequencyPeriodMonths:
            item.transactionFrequencyPeriodMonths.toString(),
        })),
      );
    }

    if (expectedFundsOrigins) {
      setExpectedFundsOriginsValues(
        expectedFundsOrigins?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          mainCounterpartiesAndWebsites: item.mainCounterpartiesAndWebsites,
          countryOfIncorporationId: item.countryOfIncorporationId,
          counterpartyBankCountryIds: item.counterpartyBankCountryIds,
          activityCountryIds: item.activityCountryIds,
          mainActivities: item.mainActivities,
          transactionReason: item.transactionReason,
          transactionType: item.transactionType,
        })),
      );
    }
  };

  const getCorporateWarningsAction = async ({
    warningGroupId,
    cursor,
  }: {
    warningGroupId: string;
    cursor: string;
  }) => {
    setSelectedWarningGroup(warningGroupId);

    await getCorporateWarning({
      id: customerId as string,
      warningGroupId,
      cursor,
    });
  };

  const applyCorporateOngoingWarningAction = async ({
    id,
    warningId,
    action,
    comment,
    matchStatus,
  }: {
    id: string;
    warningId: string;
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const response = (await applyCorporateOngoingWarning({
      orgId: customerId as string,
      id,
      warningId,
      body: {
        action,
        comment,
        employeeId: currentEmployee?.id as string,
        matchStatus,
      },
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const onOngoingRowClick = (id: string) => {
    setOngoingItemPanelVisible(true);

    getCorporateOngoingMonitoringItem({ orgId: customerId as string, id });
  };

  const formattedData = useMemo(() => {
    const ongoingMonitoringList = ongoingMonitoring?.slice(0, 10);

    const data = formatOngoingMonitroing(
      ongoingMonitoringList as OngoingMonitring[],
    );

    return data;
  }, [ongoingMonitoring]);

  const onUpdateQuestionnaire = handleSubmit(async (values) => {
    let body: Partial<Questionnarie> = {};

    if (
      currentApplicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df'
    ) {
      const questionnarieBody = getRegulatedCompanyQuestionnaireBody({
        questionnarieValues: values,
      });

      body = {
        ...body,
        ...questionnarieBody,
      };

      onSaveAccountsCurrencies();
      onSaveAccountsFlows();
      onSaveExpectedFundsOrigins();
    } else {
      if (values.typeOfBusinessLicence) {
        body.typeOfBusinessLicence = values.typeOfBusinessLicence;
      }

      if (values.hmrcMlrNumber) {
        body.hmrcMlrNumber = values.hmrcMlrNumber;
      }

      if (values.hmrcRegistrationExpiryDate) {
        body.hmrcRegistrationExpiryDate = values.hmrcRegistrationExpiryDate;
      }

      if (values.fcaNumber) {
        body.fcaNumber = values.fcaNumber;
      }

      if (values.icoNumber) {
        body.icoNumber = values.icoNumber;
      }

      if (values.numberOfBranches) {
        body.numberOfBranches = values.numberOfBranches;
      }

      if (values.numberOfAgents) {
        body.numberOfAgents = values.numberOfAgents;
      }

      if (values.mlroDetails) {
        body.mlroDetails = values.mlroDetails;
      }

      if (values.complianceConsultantDetails) {
        body.complianceConsultantDetails = values.complianceConsultantDetails;
      }

      if (values.accountantDetails) {
        body.accountantDetails = values.accountantDetails;
      }

      if (values.itProviderDetails) {
        body.itProviderDetails = values.itProviderDetails;
      }

      if (values.payoutPartnerDetails) {
        body.payoutPartnerDetails = values.payoutPartnerDetails;
      }

      if (values.transactionMonitoringKind) {
        body.transactionMonitoringKind = values.transactionMonitoringKind;
      }

      if (values.screeningKind) {
        body.screeningKind = values.screeningKind;
      }

      if (values.regulatoryInvestigationDetails) {
        body.regulatoryInvestigationDetails =
          values.regulatoryInvestigationDetails;
      }

      if (values.visitedByAuthorities) {
        body.visitedByAuthorities = values.visitedByAuthorities;
      }

      if (values.visitedByAuthoritiesDetails) {
        body.visitedByAuthoritiesDetails = values.visitedByAuthoritiesDetails;
      }

      if (values.changesInManagementOrOwnershipDetails) {
        body.changesInManagementOrOwnershipDetails =
          values.changesInManagementOrOwnershipDetails;
      }

      if (values.workingWithAnyOtherIpspDetails) {
        body.workingWithAnyOtherIpspDetails =
          values.workingWithAnyOtherIpspDetails;
      }

      if (values.auditedByExternalComplianceDetails) {
        body.auditedByExternalComplianceDetails =
          values.auditedByExternalComplianceDetails;
      }

      body.hasTransactionMonitoring = Boolean(
        values.hasTransactionMonitoring === 'yes',
      );
      body.hasScreening = Boolean(values.hasScreening === 'yes');
      body.hasRegulatoryInvestigation = Boolean(
        values.hasRegulatoryInvestigation === 'yes',
      );
      body.hasOnboardCorporateClients = Boolean(
        values.hasOnboardCorporateClients === 'yes',
      );
      body.hasOnboardMsb = Boolean(values.hasOnboardMsb === 'yes');
      body.hasPaymentsForCharities = Boolean(
        values.hasPaymentsForCharities === 'yes',
      );
      body.hasChangesInManagementOrOwnership = Boolean(
        values.hasChangesInManagementOrOwnership === 'yes',
      );
      body.hasWorkingWithAnyOtherIpsp = Boolean(
        values.hasWorkingWithAnyOtherIpsp === 'yes',
      );
      body.hasAuditedByExternalCompliance = Boolean(
        values.hasWorkingWithAnyOtherIpsp === 'yes',
      );
    }

    const response = (await updateQuestionnaire({
      id: customerId as string,
      body,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
      });
    }
  });

  const onUpdate = useCallback(() => {
    createCorporateScore(customerId as string);
  }, [customerId]);

  const getCorporateLastCalculaingScoreAction = useCallback(
    async (amlUpdateId: string) => {
      const { data } = await getCorporateAmlUpdatingItem({
        orgId: customerId as string,
        id: amlUpdateId,
      });

      if (
        data?.data.status === CustomerScoreUpdateStatus.FAIL ||
        data?.data.status === CustomerScoreUpdateStatus.SUCCESS
      ) {
        clearInterval(timeInterval);
      }

      if (data?.data.status === CustomerScoreUpdateStatus.SUCCESS) {
        getCorporateScore(customerId as string);
      }
    },
    [],
  );

  const getCorporateScoreAction = useCallback(async () => {
    (await getCorporateScore(customerId as string)) as {
      data: { data?: CustomerScore };
      error?: ResponseError;
    };
  }, [customerId]);

  useEffect(() => {
    if (!applicationSchemes) {
      getApplicationsSchemes();
    }
  }, [applicationSchemes]);

  useEffect(() => {
    if (customerId && match) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_CORPORATE_AML_TRANSACTIONS, {
          customerId,
        }),
      );
    }
  }, [customerId, match]);

  useEffect(() => {
    if (!amlGeneralInfo.values) {
      dispatch(getAmlGeneralInfo());
    }
  }, [dispatch, amlGeneralInfo.values]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      amlUpdateStatus === CustomerScoreUpdateStatus.SUCCESS &&
      activePermissions?.includes(PermissionEnum.CORPORATE_CUSTOMERS_SCORE_LIST)
    ) {
      getCorporateLastCalculaingScore(customerId as string);
    }
  }, [amlUpdateStatus, activePermissions]);

  useEffect(() => {
    if (
      changeLogsPanelIsOpen &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [changeLogsPanelIsOpen]);

  useEffect(() => {
    if (customerId) {
      if (
        activePermissions?.includes(
          PermissionEnum.CORPORATE_CUSTOMERS_SCORE_LIST,
        )
      ) {
        getCorporateScoreAction();
      }

      if (
        activePermissions?.includes(PermissionEnum.CORPORATE_CUSTOMERS_AML_LIST)
      ) {
        getCorporateOngoingMonitoring({ id: customerId });
      }
    }
  }, [customerId, activePermissions]);

  useEffect(() => {
    if (createRiskScoreIsSuccess) {
      setManualRiskScoreError('');
      setTotalRiskScore(0);
      setTotalRiskScoreLevel('');
      setManualScoreSidepanelVisible(false);
      getCorporateScoreAction();
    }
  }, [createRiskScoreIsSuccess]);

  useEffect(() => {
    if (search) {
      const parsed = queryString.parse(search);

      if (parsed.ongoingMonitoringItemId) {
        setOngoingItemPanelVisible(true);

        onOngoingRowClick(parsed.ongoingMonitoringItemId as string);
      }
    }
  }, [search]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      activePermissions?.includes(PermissionEnum.CORPORATE_CUSTOMERS_SCORE_LIST)
    ) {
      const amlUpdateId = localStorage.getItem('amlCorporateUpdateId');

      if (amlUpdateId) {
        timeInterval = setInterval(() => {
          getCorporateLastCalculaingScoreAction(amlUpdateId);
        }, 60000);
      }
    }
  }, [amlUpdateStatus, activePermissions]);

  useEffect(() => {
    if (isSuccess && aml) {
      setSuccessMessageVisible(true);

      setTimeout(() => {
        setSuccessMessageVisible(false);
      }, 6000);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (showOngoing && customerId) {
      getCorporateOngoingMonitoring({ id: customerId, take: '40' });
    }
  }, [showOngoing, customerId]);

  useEffect(() => {
    if (accountsCurrencies) {
      setAccountsCurrenciesValues(
        accountsCurrencies?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          accountOpeningPurpose: item.accountOpeningPurpose,
          plannedTransactionNature: item.plannedTransactionNature,
        })),
      );
    }
  }, [accountsCurrencies]);

  useEffect(() => {
    if (accountsFlows && accountsCurrencies) {
      const nextAccountsFlows = accountsFlows?.map((item) => ({
        id: item.id,
        transactionType: item.transactionType,
        currencyId: item.currencyId,
        expectedAnnualCreditTurnover:
          item.expectedAnnualCreditTurnover.toString(),
        maxTransferAmountPerTransaction:
          item.maxTransferAmountPerTransaction.toString(),
        transactionFrequency: item.transactionFrequency.toString(),
        transactionFrequencyPeriodMonths:
          item.transactionFrequencyPeriodMonths.toString(),
      }));

      const notCreatedCurrencies: AccountsCurrencies[] = [];

      accountsCurrencies?.forEach((item) => {
        if (
          !nextAccountsFlows?.find(
            (flow) => flow.currencyId === item.currencyId,
          )
        ) {
          notCreatedCurrencies.push(item);
        }
      });

      if (notCreatedCurrencies?.length) {
        notCreatedCurrencies?.forEach((item) => {
          nextAccountsFlows.push(
            {
              id: `accounts-flow-${uuid()}`,
              transactionType: 'INCOMING',
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: '',
              maxTransferAmountPerTransaction: '',
              transactionFrequency: '',
              transactionFrequencyPeriodMonths: '',
            },
            {
              id: `accounts-flow-${uuid()}`,
              transactionType: 'OUTGOING',
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: '',
              maxTransferAmountPerTransaction: '',
              transactionFrequency: '',
              transactionFrequencyPeriodMonths: '',
            },
          );
        });
      }

      setAccountsFlowsValues(nextAccountsFlows);
    }
  }, [accountsFlows, accountsCurrencies]);

  useEffect(() => {
    if (expectedFundsOrigins) {
      setExpectedFundsOriginsValues(
        expectedFundsOrigins?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          mainCounterpartiesAndWebsites: item.mainCounterpartiesAndWebsites,
          countryOfIncorporationId: item.countryOfIncorporationId,
          counterpartyBankCountryIds: item.counterpartyBankCountryIds,
          activityCountryIds: item.activityCountryIds,
          mainActivities: item.mainActivities,
          transactionReason: item.transactionReason,
          transactionType: item.transactionType,
        })),
      );
    }
  }, [expectedFundsOrigins]);

  useEffect(() => {
    if (deleteAccountsCurrenciesIsSuccess) {
      setSelectedAccountsCurrencyId('');
    }
  }, [deleteAccountsCurrenciesIsSuccess]);

  useEffect(() => {
    if (deleteExpectedFundsOriginsIsSuccess) {
      setSelectedFundsOriginsId('');
    }
  }, [deleteExpectedFundsOriginsIsSuccess]);

  useEffect(() => {
    if (
      baseInformation &&
      baseInformation?.questionnaire &&
      currentApplicationScheme &&
      Boolean(
        currentApplicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df',
      )
    ) {
      if (!accountsCurrencies) {
        getAccountsCurrencies(baseInformation?.questionnaire?.id);
      }

      if (!accountsFlows) {
        getAccountsFlows(baseInformation?.questionnaire?.id);
      }

      if (!expectedFundsOrigins) {
        getExpectedFundsOrigins(baseInformation?.questionnaire?.id);
      }
    }
  }, [
    baseInformation,
    currentApplicationScheme,
    accountsCurrencies,
    accountsFlows,
  ]);

  useEffect(() => {
    let title = baseInformation?.name
      ? `${baseInformation?.name}: AML & KYC`
      : 'AML & KYC';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation?.name]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST) &&
      currentApplicationScheme &&
      Boolean(
        currentApplicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df',
      )
    ) {
      if (!relationshipToTheCustomerDictionary) {
        getRelationShipToCustomerDictionary();
      }

      if (!sourceOfWealth) {
        getSourceOfWealthForCorporateDictionary();
      }

      if (!businessActivities) {
        getBusinessActivitiesDictionary();
      }

      if (!customerMainClients) {
        getRegulatedCompanyMainClientsDictionary();
      }

      if (!salesChannels) {
        getSalesChannelsDictionary();
      }

      if (!customerSourceOfFunds) {
        getCustomersSourceOfFundsDictionary();
      }

      if (!customerSourceOfWealth) {
        getCustomersSourceOfWealth();
      }

      if (!annualIncomes) {
        getCustomersAnnualIncomes();
      }

      if (!typeOfAccounts) {
        getCustomersTypeOfAccounts();
      }
    }
  }, [currentApplicationScheme, activePermissions]);

  useEffect(() => {
    if (baseInformation && baseInformation.clientScheme && applicationSchemes) {
      const currentScheme = applicationSchemes?.find(
        (item) => item.name === baseInformation.clientScheme?.name,
      );

      if (currentScheme) {
        setCurrentApplicationScheme(currentScheme);
      }
    }
  }, [baseInformation, applicationSchemes]);

  useEffect(() => {
    if (paginationIsSuccess) {
      setSelectedWarningGroup('');
    }
  }, [paginationIsSuccess]);

  useEffect(() => {
    if (baseInformation?.questionnaire) {
      const body = {
        ...baseInformation?.questionnaire,
        hasTransactionMonitoring: baseInformation?.questionnaire
          .hasTransactionMonitoring
          ? 'yes'
          : 'no',
        hasScreening: baseInformation?.questionnaire.hasScreening
          ? 'yes'
          : 'no',
        hasRegulatoryInvestigation: baseInformation?.questionnaire
          .hasRegulatoryInvestigation
          ? 'yes'
          : 'no',
        hasOnboardCorporateClients: baseInformation?.questionnaire
          .hasOnboardCorporateClients
          ? 'yes'
          : 'no',
        hasOnboardMsb: baseInformation?.questionnaire.hasOnboardMsb
          ? 'yes'
          : 'no',
        hasPaymentsForCharities: baseInformation?.questionnaire
          .hasPaymentsForCharities
          ? 'yes'
          : 'no',
        hasChangesInManagementOrOwnership: baseInformation?.questionnaire
          .hasChangesInManagementOrOwnership
          ? 'yes'
          : 'no',
        hasWorkingWithAnyOtherIpsp: baseInformation?.questionnaire
          .hasWorkingWithAnyOtherIpsp
          ? 'yes'
          : 'no',
        hasAuditedByExternalCompliance: baseInformation?.questionnaire
          .hasWorkingWithAnyOtherIpsp
          ? 'yes'
          : 'no',
        isPepOrAssociate:
          baseInformation?.questionnaire?.isPepOrAssociate?.toString() &&
          baseInformation?.questionnaire?.isPepOrAssociate?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.isPepOrAssociate === false
              ? 'no'
              : 'yes'
            : '',
        abilityMoveGovernmentFunds:
          baseInformation?.questionnaire?.abilityMoveGovernmentFunds?.toString() &&
          baseInformation?.questionnaire?.abilityMoveGovernmentFunds?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.abilityMoveGovernmentFunds ===
              false
              ? 'no'
              : 'yes'
            : '',
        belongsToGroup:
          baseInformation?.questionnaire?.belongsToGroup?.toString() &&
          baseInformation?.questionnaire?.belongsToGroup?.toString() !== 'null'
            ? baseInformation?.questionnaire?.belongsToGroup === false
              ? 'no'
              : 'yes'
            : '',
        estimatedTotalWealthEuro:
          baseInformation?.questionnaire?.estimatedTotalWealthEuro ||
          baseInformation?.questionnaire?.estimatedTotalWealthEuro?.toString() ===
            '0'
            ? baseInformation?.questionnaire?.estimatedTotalWealthEuro?.toString()
            : '',
        customerAppliesKycProcedures:
          baseInformation?.questionnaire?.customerAppliesKycProcedures?.toString() &&
          baseInformation?.questionnaire?.customerAppliesKycProcedures?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.customerAppliesKycProcedures ===
              false
              ? 'no'
              : 'yes'
            : '',
        hasBusinessActivity:
          baseInformation?.questionnaire?.hasBusinessActivity?.toString() &&
          baseInformation?.questionnaire?.hasBusinessActivity?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.hasBusinessActivity === false
              ? 'no'
              : 'yes'
            : '',
        businessActivitiesIds:
          baseInformation?.questionnaire?.hasBusinessActivity?.toString() &&
          baseInformation?.questionnaire?.hasBusinessActivity?.toString() !==
            'null' &&
          baseInformation?.questionnaire?.hasBusinessActivity === false
            ? ['NONE']
            : baseInformation?.questionnaire?.businessActivitiesIds,
        numberOfEmployees:
          baseInformation?.questionnaire?.numberOfEmployees ||
          baseInformation?.questionnaire?.numberOfEmployees?.toString() === '0'
            ? baseInformation?.questionnaire?.numberOfEmployees.toString()
            : '',
        hasAccountsInOtherFinancialInstitutions:
          baseInformation?.questionnaire?.hasAccountsInOtherFinancialInstitutions?.toString() &&
          baseInformation?.questionnaire?.hasAccountsInOtherFinancialInstitutions?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.hasAccountsInOtherFinancialInstitutions === false
              ? 'no'
              : 'yes'
            : '',
        estimatedTotalWealth:
          baseInformation?.questionnaire?.estimatedTotalWealth ||
          baseInformation?.questionnaire?.estimatedTotalWealth?.toString() ===
            '0'
            ? baseInformation?.questionnaire?.estimatedTotalWealth?.toString()
            : '',
        annualIncome:
          baseInformation?.questionnaire?.annualIncome ||
          baseInformation?.questionnaire?.annualIncome?.toString() === '0'
            ? baseInformation?.questionnaire?.annualIncome?.toString()
            : '',
      };
      reset(body);

      setDefaultQuestionaire(body);
    }
  }, [baseInformation]);

  useEffect(() => {
    if (aml && riskScoreRanges && manualScoreSidepanelVisible) {
      setTotalRiskScore(aml?.totalRiskScore);

      setTotalRiskScoreLevel(
        riskScoreRanges?.find(
          (item) =>
            aml?.totalRiskScore >= item.rangeStart &&
            aml?.totalRiskScore <= item.rangeEnd,
        )?.id as string,
      );
    }
  }, [aml, riskScoreRanges, manualScoreSidepanelVisible]);

  useEffect(() => {
    if (ongoingMonitoringPdfIsSuccess) {
      setExportDate([]);
      setExportPdfPanelVisible(false);
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'corporate-client-outgoing-monitoring',
        }),
      );
    }
  }, [ongoingMonitoringPdfIsSuccess]);

  let amlContent;

  let ongoingSidepanelContent = <div />;

  if (ongoingItemIsFetching || ongoingItemIsLoading) {
    ongoingSidepanelContent = <SidePanelLoader />;
  } else if (
    !ongoingItemIsFetching &&
    !ongoingItemIsLoading &&
    ongoingMonitoringItem
  ) {
    ongoingSidepanelContent = (
      <OngoingPanelContent
        warnings={ongoingMonitoringItem?.warnings.edges.map(
          (item) => item.node,
        )}
        warningsPagination={ongoingMonitoringItem?.warnings?.pageInfo}
        setOngoingItemPanelVisible={setOngoingItemPanelVisible}
        setBlacklistDataVisible={setBlacklistDataVisible}
        setBlacklistDataItem={setBlacklistDataItem}
        setBlacklistItemVisible={setBlacklistItemVisible}
        setBlacklistData={setBlacklistData}
        type={OrganizationType.CORPORATE}
        blacklists={ongoingMonitoringItem?.warningsGroupsBlacklists?.edges?.map(
          (item) => item?.node?.blacklist?.name,
        )}
        warningGroupId={ongoingMonitoringItem.id}
        applyError={applyError}
        applyWarningInProgress={applyWarningInProgress}
        applyOngoingWarning={applyCorporateOngoingWarningAction}
        batchApplyWarningInProgress={batchApplyWarningInProgress}
        batchApplyOngoingWarning={batchApplyCorporateOngoingWarningAction}
        batchApplyWarningIsSuccess={batchApplyWarningIsSuccess}
        onResetTransactionWarningAction={onResetTransactionWarningAction}
        resetTransactionsWarningIsLoading={resetTransactionsWarningIsLoading}
        resetTransactionsWarningIsSuccess={resetTransactionsWarningIsSuccess}
        selectedWarningId={selectedWarningId}
        status={baseInformation?.status}
        selectedWarningGroup={selectedWarningGroup}
        getWarningsAction={getCorporateWarningsAction}
        paginationInProgress={paginationInProgress || paginationIsFetching}
        getOngoingWarningById={getCorporateOngoingWarningByIdAction}
        isWarningByIdLoading={isWarningByIdLoading}
        isWarningByIdFetching={isWarningByIdFetching}
      />
    );
  }

  if (showOngoing) {
    amlContent = (
      <OngoingMonitoring
        setShowOngoing={setShowOngoing}
        onRowClick={onOngoingRowClick}
        isLoading={ongoingIsLoading || ongoingIsFetching}
        type={OrganizationType.CORPORATE}
        getOngoingMonitroing={getCorporateOngoingMonitoring}
        setExportPdfPanelVisible={setExportPdfPanelVisible}
        exportAvailable={activePermissions?.includes(
          PermissionEnum.CORPORATE_CUSTOMERS_AML_EXPORT_ACTION,
        )}
      />
    );
  } else {
    amlContent = (
      <>
        <AmlGeneralInfo
          isLoading={amlGeneralInfo.isLoading}
          // amlGeneralInfo={amlGeneralInfo.values}
          nextReviewDate={nextReviewDate}
          auditLogAllowed={activePermissions?.includes(
            PermissionEnum.AUDIT_LOG_LIST,
          )}
          setChangeLogPanelIsOpen={setChangeLogPanelIsOpen}
        />
        {baseInformation?.questionnaire &&
          Object.keys(baseInformation?.questionnaire)?.length > 0 && (
            <AdditionalInformation
              control={
                questionnaireFields?.hasRegulatoryInvestigation ||
                currentApplicationScheme?.id ===
                  'cdbb8c71-90e6-4a5e-964f-e55169e0c4df'
                  ? control
                  : undefined
              }
              setValue={setValue}
              questionnaireFields={questionnaireFields}
              currentApplicationScheme={currentApplicationScheme}
              questionnaire={baseInformation.questionnaire}
              isDirty={
                currentApplicationScheme?.id !==
                'cdbb8c71-90e6-4a5e-964f-e55169e0c4df'
                  ? !isEqual(defaultQuestionnaire, questionnaireFields)
                  : true
              }
              onResetQuestionnaire={onResetQuestionnaire}
              onUpdateQuestionnaire={onUpdateQuestionnaire}
              requestInProgress={questionnnaireRequestInProgress}
              status={baseInformation?.status}
              errors={errors}
              clearErrors={clearErrors}
              relationshipToTheCustomerDictionary={
                relationshipToTheCustomerDictionary
              }
              countries={countriesList}
              sourceOfWealth={sourceOfWealth}
              baseCurrency={baseCurrency}
              businessActivities={businessActivities}
              customerMainClients={customerMainClients}
              salesChannels={salesChannels}
              customerSourceOfFunds={customerSourceOfFunds}
              customerSourceOfWealth={customerSourceOfWealth}
              annualIncomes={annualIncomes}
              totalIncomeError={totalIncomeError}
              setTotalIncomeError={setTotalIncomeError}
              typeOfAccounts={typeOfAccounts}
              accountsCurrenciesIsLoading={accountsCurrenciesIsLoading}
              accountsCurrenciesIsFetching={accountsCurrenciesIsFetching}
              accountsCurrenciesValues={accountsCurrenciesValues}
              setAccountsCurrenciesValues={setAccountsCurrenciesValues}
              currencies={currencies}
              onDeleteAccountCurrencies={onDeleteAccountCurrencies}
              selectedAccountsCurrencyId={selectedAccountsCurrencyId}
              deleteAccountsCurrenciesInProgress={
                deleteAccountsCurrenciesInProgress
              }
              setAccountsFlowsValues={setAccountsFlowsValues}
              accountsFlowsValues={accountsFlowsValues}
              accountsFlowsIsLoading={accountsFlowsIsLoading}
              accountsFlowsIsFetching={accountsFlowsIsFetching}
              currenciesList={currenciesList}
              expectedFundsOriginsIsLoading={expectedFundsOriginsIsLoading}
              expectedFundsOriginsIsFetching={expectedFundsOriginsIsFetching}
              deleteExpectedFundsOriginsInProgress={
                deleteExpectedFundsOriginsInProgress
              }
              onDeleteExpectedFundsOrigins={onDeleteExpectedFundsOrigins}
              selectedExpectedFundsOriginsId={selectedExpectedFundsOriginsId}
              expectedFundsOriginsValues={expectedFundsOriginsValues}
              setExpectedFundsOriginsValues={setExpectedFundsOriginsValues}
              createAccountsCurrenciesIsProgress={
                createAccountsCurrenciesIsProgress
              }
              updateAccountsCurrenciesInProgress={
                updateAccountsCurrenciesInProgress
              }
              createAccountsFlowsIsProgress={createAccountsFlowsIsProgress}
              updateAccountsFlowsInProgress={updateAccountsFlowsInProgress}
              createExpectedFundsOriginsInProgress={
                createExpectedFundsOriginsInProgress
              }
              updateExpectedFundsOriginsInProgress={
                updateExpectedFundsOriginsInProgress
              }
            />
          )}
        {activePermissions?.includes(
          PermissionEnum.CORPORATE_CUSTOMERS_SCORE_LIST,
        ) && (
          <Card
            title="Risk scoring"
            isLoading={isLoading}
            className={cn.riskScoringCard}
          >
            <RiskScoring
              aml={aml}
              onUpdate={onUpdate}
              updateInProgress={requestInProgress}
              updateIsSuccess={isSuccess}
              amlUpdateStatus={amlUpdateStatus}
              calculateAllowed={activePermissions?.includes(
                PermissionEnum.CORPORATE_CUSTOMERS_SCORE_AUTO_ON_ACTION,
              )}
              status={baseInformation?.status}
              isLoading={isLoading}
              users={users}
              setManualScoreSidepanelVisible={setManualScoreSidepanelVisible}
              setRiskScoreChangeLogsSidepanelVisible={
                setRiskScoreChangeLogsSidepanelVisible
              }
              manualScoreAllowed={activePermissions?.includes(
                PermissionEnum.CORPORATE_CUSTOMERS_SCORE_CREATE_ACTION,
              )}
            />
          </Card>
        )}

        {activePermissions?.includes(
          PermissionEnum.CORPORATE_CUSTOMERS_AML_LIST,
        ) && (
          <Card
            titleContent={
              <div className={cn.ongoingTitleWrapper}>
                <div className={cn.ongoingTitleLeft}>
                  <div className={cn.ongoingTitle}>Ongoing monitoring</div>
                  <button
                    type="button"
                    className={cn.ongoingShowMore}
                    onClick={() => setShowOngoing(true)}
                  >
                    Show more
                    <IconRight />
                  </button>
                </div>
                <div className={cn.ongoingTitleRight}>
                  {activePermissions?.includes(
                    PermissionEnum.CORPORATE_CUSTOMERS_AML_EXPORT_ACTION,
                  ) && (
                    <button
                      type="button"
                      className={cn.ongoingExport}
                      onClick={() => setExportPdfPanelVisible(true)}
                    >
                      <IconUpload />
                      Export PDF
                    </button>
                  )}
                </div>
              </div>
            }
          >
            <div className={cn.ongoingTableWrapper}>
              <Table
                columns={ongoingColumns}
                data={formattedData || []}
                sort={{}}
                onSort={() => {}}
                filters={{}}
                tableBodyClassName={
                  ongoingMonitoring?.length ? '' : cn.emptyOngoing
                }
                columnsLabels={ongoingColumnsLabels}
                onFilterChange={() => {}}
                onRowClick={onOngoingRowClick}
                isLoading={ongoingIsLoading || ongoingIsFetching}
              />
            </div>
          </Card>
        )}

        <InitialCdd
          businessPartners={businessPartners}
          baseCurrency={baseCurrency?.name}
          onUpdateRange={onUpdateRange}
          status={baseInformation?.status}
          baseInformation={baseInformation}
          clientScheme={currentApplicationScheme}
          currencies={currencies}
          updateRangesInProgress={updateRangesInProgress}
          onUpdateCorporateInfo={onUpdateCorporateInfo}
          updateInfoIsLoading={updateInfoIsLoading}
        />
        <AmlAccountsTransactionStatement
          title="Red flag history"
          matchPath={ROUTES.CUSTOMERS_CORPORATE_AML_CARDS}
          transactionsPath={ROUTES.CUSTOMERS_CORPORATE_AML_TRANSACTIONS}
          cardsTransactionsPath={ROUTES.CUSTOMERS_CORPORATE_AML_CARDS}
          customerType={CustomerType.CORPORATE}
          accounts={baseInformation?.accounts}
          cards={baseInformation?.cards}
          cardsEnabled={cardsEnabled}
          sendMessage={sendMessage}
        />
      </>
    );
  }

  return (
    <div className={cn.amlContent}>
      {amlContent}
      {successMessageVisible && (
        <div className={cn.successAppliedMessage}>
          <IconSelected />
          <div>Risk Score successfully updated to the Customer</div>
        </div>
      )}
      <SidePanel
        type={SidePanelType.Customers}
        closeOnClickOutside
        isOpen={exportPdfPanelVisible}
        onClose={() => setExportPdfPanelVisible(false)}
        title="Export Ongoing monitoring"
        sidepanelExpandedKey="export-ongoing-panel"
        footer={
          <>
            <ButtonPrimary
              text={ongoingMonitoringPdfIsLoading ? 'Loading...' : 'Export'}
              onClick={() => getOngoingMonitoringPdfAction()}
              disabled={ongoingMonitoringPdfIsLoading}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={() => setExportPdfPanelVisible(false)}
              bold
            />
          </>
        }
      >
        <div className={cn.datePickerRangeWrapper}>
          <div className={cn.datePickerRangeWrapperTitle}>Select period</div>
          <div className={cn.dateRangePickerWrapperContent}>
            <DateInputRange
              title="Select period"
              placeholder="Select period"
              values={exportDate}
              onChangeRange={(date) =>
                setExportDate(date.map((item) => item.toISOString()))
              }
              className={cn.datePickerItem}
              withoutTwoPickers
              withoutCustomSelector
            />
            <IconCalendar className={cn.iconCalendar} />
          </div>
        </div>
      </SidePanel>
      <SidePanel
        type={SidePanelType.Customers}
        closeOnClickOutside
        isOpen={ongoingItemPanelVisible}
        onClose={() => {
          setOngoingItemPanelVisible(false);
          setApplyError('');
        }}
        title="Ongoing monitoring"
        sidepanelExpandedKey="ongoing-panel"
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setOngoingItemPanelVisible(false);
              setApplyError('');
            }}
            bold
          />
        }
      >
        {ongoingSidepanelContent}
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistDataVisible}
        onClose={() => {
          setOngoingItemPanelVisible(true);
          setBlacklistDataVisible(false);
          setBlacklistData(undefined);
        }}
        title={blacklistData?.fullName as string}
        sidepanelExpandedKey="ongoing-blacklist-data"
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setOngoingItemPanelVisible(true);
              setBlacklistDataVisible(false);
              setBlacklistData(undefined);
            }}
            bold
          />
        }
      >
        <BlacklistDataContent data={blacklistData} />
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistItemVisible}
        onClose={() => {
          setBlacklistDataItem(undefined);
          setBlacklistItemVisible(false);
          setOngoingItemPanelVisible(true);
        }}
        title="User Details"
        sidepanelExpandedKey="ongoing-blacklist-data"
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setBlacklistDataItem(undefined);
              setBlacklistItemVisible(false);
              setOngoingItemPanelVisible(true);
            }}
            bold
          />
        }
        withoutExpand
        expanded
      >
        <BlacklistUserInfo info={blacklistItem} isLoading={false} />
      </SidePanel>
      <SidePanel
        isOpen={changeLogsPanelIsOpen}
        onClose={() => {
          setChangeLogPanelIsOpen(false);
        }}
        title="Customer change logs"
        sidepanelExpandedKey="customers-change-logs"
        type={SidePanelType.Accounts}
        footer={
          <div>
            <ButtonPseudo
              text="Close"
              onClick={() => setChangeLogPanelIsOpen(false)}
              bold
            />
          </div>
        }
        closeOnClickOutside
      >
        <>
          {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <ChangeLogs
              entityType="ORGANIZATION"
              entityId={customerId as string}
              changeLogsIsLoading={changeLogsIsLoading}
              changeLogs={changeLogs}
              activePermission={activePermissions}
              users={users}
              pagination={changeLogsPagination}
              changeLogsIsFetching={changeLogsIsFetching}
              getChangeLogs={getChangeLogsAction}
            />
          )}
          {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <PermissionDenied />
          )}
        </>
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={manualScoreSidepanelVisible}
        onClose={() => {
          setManualScoreSidepanelVisible(false);
          setTotalRiskScore(0);
          setTotalRiskScoreLevel('');
          setManualRiskScoreError('');
        }}
        title="Set Risk Score manually"
        sidepanelExpandedKey="manual-risk-score"
        errorSidepanel={manualRiskScoreError}
        footer={
          <>
            <ButtonPrimary
              text={createRiskScoreIsLoading ? 'Loading...' : 'Apply'}
              disabled={createRiskScoreIsLoading}
              onClick={createCorporateManualRiskScoreAction}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={() => {
                setManualScoreSidepanelVisible(false);
                setTotalRiskScore(0);
                setTotalRiskScoreLevel('');
                setManualRiskScoreError('');
              }}
              bold
            />
          </>
        }
      >
        <>
          <div className={cn.manualRiskScoreDescription}>
            Select a Risk level or enter an exact risk score value.
          </div>
          <div className={cn.manualRiskScoreFormGroup}>
            <Select
              name="riskLevel"
              title="Risk Level"
              value={totalRiskScoreLevel}
              list={riskScoreRanges?.map((item) => ({
                name: `${item.name.charAt(0)}${item.name
                  .slice(1)
                  .toLowerCase()} (${item.rangeStart} - ${item.rangeEnd})`,
                value: item.id,
                suffix: `${item.rangeStart} - ${item.rangeEnd}`,
              }))}
              onChange={(e) => {
                setTotalRiskScoreLevel(e.target.value as string);
                setTotalRiskScore(
                  riskScoreRanges?.find((item) => item.id === e.target.value)
                    ?.rangeStart as number,
                );
              }}
              fullWidth
            />
          </div>
          <div className={cn.manualRiskScoreFormGroup}>
            <InputNumber
              name="riskScore"
              title="Risk Score"
              value={totalRiskScore}
              placeholder="Enter risk score"
              onChange={(e) => {
                clearTimeout(manualRiskScoreTimeout);
                const value =
                  Number(e.target.value) > 100 ? 100 : Number(e.target.value);

                setTotalRiskScore(value);

                manualRiskScoreTimeout = setTimeout(() => {
                  setTotalRiskScoreLevel(
                    riskScoreRanges?.find(
                      (item) =>
                        value >= item.rangeStart && value <= item.rangeEnd,
                    )?.id as string,
                  );
                }, 300);
              }}
            />
          </div>
        </>
      </SidePanel>
      <AmlChangeLogs
        panelIsOpen={riskScoreChangeLogsSidepanelVisible}
        handleCloseAmlChangeLogsPanel={handleCloseAmlChangeLogsPanel}
        getAmlChangeLogs={getCustomerAmlChangeLogsAction}
        amlChangeLogsIsLoading={amlScoresHistoryIsLoading}
        amlChangeLogs={amlChangeLogs}
        amlChangeLogsPagination={amlChangeLogsPagination}
        amlScoresHistoryIsFetching={amlScoresHistoryIsFetching}
        users={users}
        type={CustomerType.CORPORATE}
        organizationId={customerId}
      />
      <div id="apply-all-popup" />
      <div id="signature-popup" />
    </div>
  );
};

export default Aml;
